
/**
 *New Project form to create a new project .
 */
interface countryState {
  name: string;
  id: number;
}
interface country {
  name: string;
  id: number;
  state: Array<countryState>;
}

import Vue from "vue";
import { Component, Prop, Watch } from "vue-property-decorator";

import Multiselect from "vue-multiselect";
import { mapGetters, mapActions } from "vuex";
import { required, minLength } from "vuelidate/lib/validators";
import ModelNewProject from "@/model/ModelNewProject";
import DateToIsoString from "@/utils/DateToIsoString";
import ModelUser from "@/model/ModelUser";

@Component({
  name: "NewProjectModal",
  components: { Multiselect },
  computed: {
    ...mapGetters("projects", {
      statusList: "GetAllStatus",
      typesList: "GetProjectTypes",
      countrysList: "GetAllAvailableCountrys",
      allUserOptions: "GetAllUserOption"
    })
  },
  methods: {
    ...mapActions("projects", {
      CreateNewProject: "CreateNewProject",
      getAllStatusList: "GetAllStatusFromBackEnd",
      getAllProjectTypes: "GetAllTypesFromBackEnd",
      GetAllCountrys: "GetCountryList",
      GetAllUser: "GetAllUsersFromTheProject"
    })
  }
})
export default class NewProjectModal extends Vue {
  // Vuex
  readonly statusList!: Array<object>;
  readonly typesList!: Array<object>;
  readonly countrysList!: Array<object>;
  private readonly allUserOptions!: Array<ModelUser>;

  private CreateNewProject!: Function;
  private getAllStatusList!: Function;
  private getAllProjectTypes!: Function;
  private GetAllCountrys!: Function;
  private GetAllUser!: Function;

  // Props
  @Prop({ type: Number, default: null }) mainProjectId!: number;

  // Data
  selected: null | object = null;
  typeSelected = 1;
  newProject: ModelNewProject = new ModelNewProject();

  localStartDate = "";
  localEndDate = "";
  apiExportTime = "";
  localCountrySelected: null | country = null;
  localStateSelected: null | countryState = null;
  loading = false;
  showPassword = true;

  LocalProjectTypesOptions: Array<any> = [];
  showMatrixReportButton = false;

  /* ---------------- Logic: Token vs Credentials ---------------- */
  // Token-Modus aktiv, wenn Token befüllt ist
  get isTokenMode(): boolean {
    return !!(this.newProject.apiToken && this.newProject.apiToken.length > 0);
  }
  // Credentials-Modus aktiv, wenn (User oder Passwort) befüllt sind und KEIN Token aktiv ist
  get isCredsMode(): boolean {
    const u = this.newProject.apiUser || "";
    const p = this.newProject.apiPassword || "";
    return (u.length > 0 || p.length > 0) && !this.isTokenMode;
  }

  // Disable-Zustände: anfangs sind alle false (alles aktiv)
  get disableToken(): boolean {
    return this.isCredsMode; // sobald Creds aktiv, Token sperren
  }
  get disableCreds(): boolean {
    return this.isTokenMode; // sobald Token aktiv, Creds sperren
  }

  // Required-Flags nur, wenn apiExportActive true ist – und je nach Modus
  get requiredApiToken(): boolean {
    return !!this.newProject.apiExportActive && this.isTokenMode;
  }
  get requiredApiUser(): boolean {
    return !!this.newProject.apiExportActive && this.isCredsMode;
  }
  get requiredApiPassword(): boolean {
    return !!this.newProject.apiExportActive && this.isCredsMode;
  }
  get requiredApiCert(): boolean {
    return !!this.newProject.apiExportActive && this.isCredsMode;
  }

  // Sobald in User/Password getippt wird: Token leeren (und damit disabled werden)
  @Watch("newProject.apiUser")
  onApiUserChanged(val: string) {
    if ((val || "").length > 0) {
      this.newProject.apiToken = "";
    }
  }
  @Watch("newProject.apiPassword")
  onApiPasswordChanged(val: string) {
    if ((val || "").length > 0) {
      this.newProject.apiToken = "";
    }
  }
  // Sobald in Token getippt wird: User & Password leeren (und damit disabled werden)
  @Watch("newProject.apiToken")
  onApiTokenChanged(val: string) {
    if ((val || "").length > 0) {
      this.newProject.apiUser = "";
      this.newProject.apiPassword = "";
    }
  }

  /* ---------------- Rest: unverändert (gekürzt, dein Code bleibt) ---------------- */
  @Watch("typesList", { immediate: true })
  handleProjectTypes() {
    this.LocalProjectTypesOptions = this.typesList.map((item: any) => {
      item.label = this.$t(`projects.types.${item.name}`);
      return item;
    });
  }

  get isTheFormValid() {
    if (!this.mainProjectId) {
      return (
        this.$v.$invalid ||
        !this.newProject.projectTypeId ||
        !this.newProject.projectStatusId ||
        !this.localCountrySelected ||
        !this.localStartDate ||
        !this.localEndDate
      );
    } else {
      return (
        this.$v.$invalid ||
        !this.newProject.projectTypeId ||
        !this.newProject.projectStatusId ||
        !this.localCountrySelected ||
        !this.localStateSelected ||
        !this.localStartDate ||
        !this.localEndDate ||
        !this.newProject.leader
      );
    }
  }

  validations() {
    if (this.mainProjectId !== null) {
      return {
        newProject: {
          name: { required, minLength: minLength(1) },
          directionNameStart: { required, minLength: minLength(1) },
          directionNameEnd: { required, minLength: minLength(1) },
          directionPosStart: { required, minLength: minLength(1) },
          directionPosEnd: { required, minLength: minLength(1) },
          date_start_time: { required, minLength: minLength(1) },
          date_end_time: { required, minLength: minLength(1) },
          description: { required, minLength: minLength(1) }
        }
      };
    } else {
      return {
        newProject: {
          name: { required, minLength: minLength(1) },
          description: { required, minLength: minLength(1) }
        }
      };
    }
  }

  created() {
    this.getAllStatusList();
    this.getAllProjectTypes();
    this.GetAllUser();
  }
  mounted() {
    this.GetAllCountrys();
  }

  async createNewProject() {
    this.$emit("updateStatus", null);
    if (this.$v.$invalid) return false;

    if (!this.localCountrySelected) return;

    try {
      this.loading = true;
      this.newProject.parentProjectId = this.mainProjectId;

      // Dates
      this.newProject.dateStart = this.localStartDate;
      this.newProject.dateEnd = this.localEndDate;

      // Country/State
      this.newProject.countryId = this.localCountrySelected.id;
      this.newProject.federalStateId = this.localStateSelected ? this.localStateSelected.id : null;

      // Time to ISO
      if ((this.newProject as any).projectTypeId.name !== "main_project") {
        let startDate: Date | string = new Date(`${this.localStartDate} ${this.newProject.date_start_time}`);
        let endDate: Date | string = new Date(`${this.localEndDate} ${this.newProject.date_end_time}`);
        this.newProject.dateStart = DateToIsoString(startDate);
        this.newProject.dateEnd = DateToIsoString(endDate);
      } else {
        this.newProject.dateStart = DateToIsoString(new Date(this.localStartDate));
        this.newProject.dateEnd = DateToIsoString(new Date(this.localEndDate));
      }

      // apiExportTime (HH:mm[:ss]) -> heute + Zeit -> ISO
      if (this.newProject.apiExportTime) {
        const [h, m, s = "0"] = this.newProject.apiExportTime.split(":");
        const dt = new Date();
        dt.setHours(parseInt(h || "0", 10));
        dt.setMinutes(parseInt(m || "0", 10));
        dt.setSeconds(parseInt(s || "0", 10));
        dt.setMilliseconds(0);
        this.newProject.apiExportTime = DateToIsoString(dt);
      }

      // IDs
      (this.newProject as any).projectStatusId = (this.newProject as any).projectStatusId.id;
      (this.newProject as any).projectTypeId = (this.newProject as any).projectTypeId.id;
      (this.newProject as any).leaderId = this.newProject.leader ? (this.newProject.leader as any).id : "";

      const res = await this.CreateNewProject(this.newProject);
      this.loading = false;

      if (res.status === 201) {
        this.$emit("updateStatus", { class: "success", msg: this.$t("projects.statusMsg.createdSuccess") });
        this.$bvModal.hide("newProject");
      } else {
        this.$emit("updateStatus", { class: "success", msg: res });
        this.$bvModal.hide("newProject");
      }
    } catch (error) {
      this.$emit("updateStatus", { class: "success", msg: error });
      this.loading = false;
      this.$bvModal.hide("newProject");
    }
  }

  resetForm() {
    this.selected = null;
    this.localStartDate = "";
    this.localEndDate = "";
    this.typeSelected = 1;
    this.localCountrySelected = null;
    this.localStateSelected = null;
    this.newProject = new ModelNewProject();
    (this.newProject as any).projectTypeId = this.mainProjectId
      ? null
      : (this.typesList as any).find((item: any) => item.name === "main_project");
  }

  filterTheProjectType() {
    if (this.mainProjectId) {
      return this.LocalProjectTypesOptions
        .filter((item: any) => item.name !== "main_project")
        .sort((a, b) => a.label.localeCompare(b.label));
    } else {
      return this.LocalProjectTypesOptions.filter((item: any) => item.name === "main_project");
    }
  }

  changeTypeToShowReportButton(projectType: any) {
    this.showMatrixReportButton = projectType.label === "WVZ";
  }
}
